import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {useFormikContext} from 'formik';
import {DrawerFields} from 'components/customer/Product/entity/Drawer';
import {toNumber} from 'components/customer/Product/Drawer/helpers';
import {DrawerParameters} from 'components/customer/Product/entity/DrawerParameters';
import {DrawerCarcase} from 'components/customer/Product/Drawer/DrawerCarcase';
import {DrawerPresets} from 'components/customer/Product/Drawer/DrawerPresets';
import {SelectedDrawer} from 'components/customer/Product/Drawer/SelectedDrawer';
import {MoreInfoStatic} from 'shared/components/MoreInfo/MoreInfo';
import {
    FaceDraftProvider,
    FaceLimits,
    HeldFace,
} from 'components/customer/Product/Drawer/FaceDraftContext';
import {useDrawerDefaults} from 'components/customer/Product/Drawer/DrawerDefaultsContext';
import {useFaceRange} from 'components/customer/Product/Drawer/helpers/useFaceRange';
import {setFaceHeightErrors} from 'components/customer/Product/store/drawerRunnerSlice';
import {useAppDispatch} from 'store/customer';
import {Icon} from 'shared/helpers';
import {
    Card,
    CardHeader,
    InfoText,
    ResetButton,
    Spacer,
} from 'components/customer/Product/Drawer/layoutStyles';

const LAYOUT_INFO = `Click a face to select it, then type a height or drag its edges. Gaps and margins are drawn to scale.`;

interface DrawerLayoutProps {
    drawerParams: DrawerParameters;
    drawerAmount: number;
}

// the cabinet drawn to scale, beside the controls for the selected drawer
export const DrawerLayout = ({
    drawerParams,
    drawerAmount,
}: DrawerLayoutProps) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const {values, setFieldValue, setTouched} =
        useFormikContext<DrawerFields>();
    const {resetRunners} = useDrawerDefaults();
    const {rangeFor, check} = useFaceRange(drawerParams);
    const dispatch = useAppDispatch();

    const styleFieldName = drawerParams.drawerStyleFieldName;
    const faceHeightFieldName = drawerParams.drawerFaceHeightFieldName;

    const selectHandler = useCallback((index: number) => {
        setSelectedIndex(index);
    }, []);

    const layout = useMemo(
        () =>
            JSON.stringify(
                (values.drawers ?? []).map((drawer) => [
                    toNumber(drawer[String(faceHeightFieldName)]),
                    toNumber(drawer[String(styleFieldName)]),
                ])
            ),
        [values.drawers, faceHeightFieldName, styleFieldName]
    );

    // as the product's own validation errors, a held height shows on its
    // field like any other and stops the product saving
    const holdHandler = useCallback(
        (held: HeldFace[]) => {
            dispatch(
                setFaceHeightErrors(
                    held.map(({index, message}) => ({
                        fields: [`${faceHeightFieldName}[${index}]`],
                        message,
                    }))
                )
            );
        },
        [dispatch, faceHeightFieldName]
    );

    // the store outlives this product, so must not keep its held heights
    useEffect(
        () => () => {
            dispatch(setFaceHeightErrors([]));
        },
        [dispatch]
    );

    const limits = useMemo<FaceLimits>(
        () => ({check, layout, onHold: holdHandler, onReject: selectHandler}),
        [check, layout, holdHandler, selectHandler]
    );

    // nothing to return: every face is already sharing the space
    const allAuto = useMemo(() => {
        const drawers = values.drawers ?? [];

        return (
            drawers.length > 0 &&
            drawers.every(
                (drawer) => toNumber(drawer[String(styleFieldName)]) === 1
            )
        );
    }, [values.drawers, styleFieldName]);

    const equalHandler = useCallback(() => {
        void setTouched({drawers: true});

        (values.drawers ?? []).forEach((drawer, index) => {
            void setFieldValue(`drawers[${index}].${styleFieldName}`, 1);
        });

        resetRunners();
    }, [
        values.drawers,
        setFieldValue,
        setTouched,
        styleFieldName,
        resetRunners,
    ]);

    // a drawer can be removed while it is selected
    const safeIndex = Math.min(selectedIndex, Math.max(0, drawerAmount - 1));

    return (
        <Card data-cy="drawer-layout">
            <CardHeader>
                Drawer Layout
                <MoreInfoStatic
                    hoverText={<InfoText>{LAYOUT_INFO}</InfoText>}
                />
                <Spacer />
                {drawerAmount > 1 ? (
                    <ResetButton
                        variant="link"
                        className="button-blue btn-sm"
                        data-cy="drawer-make-equal"
                        onClick={equalHandler}
                        disabled={allAuto}>
                        <Icon iconName="Reverse-Refresh.svg" color="white" />
                        <span>Make all equal</span>
                    </ResetButton>
                ) : null}
            </CardHeader>
            <Body>
                <DrawerPresets drawerParams={drawerParams} />
                {/* the drawing and the panel edit the same height, so the half-typed text is shared rather than held by either */}
                <FaceDraftProvider limits={limits}>
                    <DrawerCarcase
                        drawerParams={drawerParams}
                        selectedIndex={safeIndex}
                        onSelect={selectHandler}
                        rangeFor={rangeFor}
                    />
                    <SelectedDrawer
                        drawerParams={drawerParams}
                        drawerAmount={drawerAmount}
                        selectedIndex={safeIndex}
                    />
                </FaceDraftProvider>
            </Body>
        </Card>
    );
};

const Body = styled.div`
    display: flex;
    gap: 16px;
    padding: 16px 14px 20px;
    align-items: flex-start;
    /* the presets take a full line of their own, so the diagram and panel share
       the one below */
    flex-wrap: wrap;
`;
